import { portalLevelPermissions } from 'web-shared/modules/Permissions/PortalLevelPermissions';
import PERMISSIONS_LIST from 'web-shared/modules/Permissions/permissions-list';
import { useUserCompanies } from 'web-shared/reducers/user-companies/hooks';
import { useUserRole } from 'web-shared/hooks/useUserRole';
import { AppointmentStatusChangeEvent } from '../../modules/Appointments/api/enums/appointment-event';
import { NON_EDITABLE_APPOINTMENT_STATUS_EVENTS } from './const';

export const useHealthcareAccess = () => {
  const { companies = [] } = useUserCompanies();
  const { isAdminUser } = useUserRole();
  /**
   * Determine if user has view access
   */
  const hasHealthcareAccess = () => {
    return portalLevelPermissions.isPermissionEnabled(PERMISSIONS_LIST.healthcare_view_access);
  };

  /**
   * Determine if user has access to manage appointments
   * This permission is required to create, update, or delete appointments
   */
  const hasManageAppointmentsAccess = () => {
    return hasHealthcareAccess() && portalLevelPermissions.isPermissionEnabled(PERMISSIONS_LIST.healthcare_manage_appointments);
  };

  /**
   * Determine if user has access to manage medical devices
   * This permission is required to create, update, or delete medical devices
   */
  const hasManageMedicalDevicesAccess = () => {
    return hasHealthcareAccess() && portalLevelPermissions.isPermissionEnabled(PERMISSIONS_LIST.healthcare_manage_medical_devices);
  };

  /**
   * Determine if user has access to edit appointment
   */
  const hasAppointmentEditAccess = <T extends { last_status_event_kind: AppointmentStatusChangeEvent }>(appointment: T) => {
    return hasManageAppointmentsAccess()
      && !NON_EDITABLE_APPOINTMENT_STATUS_EVENTS.includes(appointment.last_status_event_kind);
  };

  /**
   * Determine if user has access to appointments where medical device is involved
   */
  const hasMedicalDeviceAppointmentsAccess = <T extends {
    appointment_enabled?: boolean;
    appointments_count?: number;
  }>(medicalDevice: T) => {
    return hasHealthcareAccess()
      && (medicalDevice.appointment_enabled || !!medicalDevice.appointments_count);
  };

  /**
   * Determine if user has access to assign medical devices to appointments
   */
  const hasAssignMedicalDevicesToAppointmentsAccess = () => {
    return hasHealthcareAccess()
      && portalLevelPermissions.isPermissionEnabled(PERMISSIONS_LIST.healthcare_assign_medical_devices_to_appointments);
  };

  /**
   * Determine if user can manage medtech attributes like medical device family, product family and product line
   */
  const hasManageMedTechAttributesAccess = () => {
    return hasManageMedicalDevicesAccess()
      && portalLevelPermissions.isPermissionEnabled(PERMISSIONS_LIST.healthcare_manage_medtech_attributes);
  };

  /**
   * Determines if the user has access to transfer medical device
   */
  const hasMedicalDeviceTransferAccess = () => {
    return hasManageMedicalDevicesAccess() && (isAdminUser || companies.length > 1);
  };

  return {
    hasHealthcareAccess,
    hasMedicalDeviceAppointmentsAccess,
    hasManageAppointmentsAccess,
    hasManageMedicalDevicesAccess,
    hasAppointmentEditAccess,
    hasAssignMedicalDevicesToAppointmentsAccess,
    hasManageMedTechAttributesAccess,
    hasMedicalDeviceTransferAccess,
  };

};